import { WaypointType } from '@treadinc/horizon-api-spec';

import {
  isDefaultSiteUsages,
  type JobSiteUsage,
  type OrderSiteUsage,
} from '~hooks/useSiteUsages/models';
import { JOB_COLOR_PALETTE } from '~pages/Admin/Geo/mapIcons';

interface MarkerData {
  id: string;
  jobUuid: string;
  lat: number;
  lng: number;
  siteName: string;
  jobId: string;
  letter: string;
  color: string;
  waypointType: WaypointType | 'both';
  isDefaultSite?: boolean;
}

/** Minimal job shape needed to color and label order markers. */
interface OrderMarkerJob {
  id: string;
  jobId: string;
}

/** A->Z, then AA->ZZ, for labeling jobs by their index in the order. */
export function getJobLetter(index: number): string {
  if (index < 26) return String.fromCharCode(65 + index);
  return (
    String.fromCharCode(65 + Math.floor(index / 26) - 1) +
    String.fromCharCode(65 + (index % 26))
  );
}

interface BuildOrderMarkersParams {
  /** When set, only this job's site usages are shown (single-job mode). */
  selectedJobUuid: string | null;
  /** Site usages for the selected job (fetched per-job); null when none. */
  selectedJobSiteUsages: JobSiteUsage[] | null;
  /** Order-level site usages, used when no job is selected. */
  siteUsages: OrderSiteUsage[];
  jobs: OrderMarkerJob[];
  movingSiteInfo: { siteId: string } | null;
  movingSitePosition: { position: { lat: number; lng: number } } | null;
}

/**
 * Builds the order route-map markers: one per (site, waypoint type), collapsed
 * to `'both'` when a site is used for both pickup and drop-off, colored and
 * lettered per job, flagged as a default (candidate) site when every usage is a
 * default, and repositioned to the live moving-site location when applicable.
 */
export function buildOrderMarkers({
  selectedJobUuid,
  selectedJobSiteUsages,
  siteUsages,
  jobs,
  movingSiteInfo,
  movingSitePosition,
}: BuildOrderMarkersParams): MarkerData[] {
  const result: MarkerData[] = [];
  const siteWaypointMap = new Map<
    string,
    { types: Set<string>; markers: MarkerData[] }
  >();

  const addMarker = (
    siteId: string,
    wpId: string,
    wpType: string,
    lat: number,
    lng: number,
    siteName: string,
    jobUuid: string,
    jobId: string,
    letter: string,
    color: string,
    isDefaultSite: boolean,
  ) => {
    if (!siteWaypointMap.has(siteId)) {
      siteWaypointMap.set(siteId, { types: new Set(), markers: [] });
    }
    const entry = siteWaypointMap.get(siteId)!;
    entry.types.add(wpType);
    entry.markers.push({
      id: `${jobUuid || 'order'}-${wpId}`,
      jobUuid,
      lat,
      lng,
      siteName,
      jobId,
      letter,
      color,
      waypointType: wpType as WaypointType,
      isDefaultSite,
    });
  };

  if (selectedJobUuid) {
    // A specific job is selected — use its site_usages from the API.
    const jobIndex = jobs.findIndex((j) => j.id === selectedJobUuid);
    const letter = jobIndex >= 0 ? getJobLetter(jobIndex) : '';
    const color =
      jobIndex >= 0 ? JOB_COLOR_PALETTE[jobIndex % JOB_COLOR_PALETTE.length] : '#2221BB';
    const job = jobIndex >= 0 ? jobs[jobIndex] : null;
    for (const su of selectedJobSiteUsages ?? []) {
      if (!su.site.lat || !su.site.lon) continue;
      const isMoving =
        movingSiteInfo?.siteId &&
        su.site.id === movingSiteInfo.siteId &&
        movingSitePosition;
      const lat = isMoving ? movingSitePosition.position.lat : Number(su.site.lat);
      const lng = isMoving ? movingSitePosition.position.lng : Number(su.site.lon);
      const isDefaultSite = isDefaultSiteUsages(su.usages);
      for (const usage of su.usages) {
        addMarker(
          su.site.id,
          `${su.site.id}-${usage.waypointType}`,
          usage.waypointType,
          lat,
          lng,
          su.site.name ?? '',
          selectedJobUuid,
          job?.jobId ?? '',
          letter,
          color,
          isDefaultSite,
        );
      }
    }
  } else {
    // No job selected — build markers from site_usages, attributing each
    // site to its first associated job for per-job color differentiation.
    const jobIndexById = new Map(jobs.map((j, i) => [j.id, i]));
    for (const su of siteUsages) {
      if (!su.site.lat || !su.site.lon) continue;
      const isMoving =
        movingSiteInfo?.siteId &&
        su.site.id === movingSiteInfo.siteId &&
        movingSitePosition;
      const lat = isMoving ? movingSitePosition.position.lat : Number(su.site.lat);
      const lng = isMoving ? movingSitePosition.position.lng : Number(su.site.lon);
      const isDefaultSite = isDefaultSiteUsages(su.usages);

      for (const usage of su.usages) {
        const firstJob = usage.jobs[0];
        const jobIndex = firstJob ? (jobIndexById.get(firstJob.id) ?? -1) : -1;
        const letter = jobIndex >= 0 ? getJobLetter(jobIndex) : '';
        const color =
          jobIndex >= 0
            ? JOB_COLOR_PALETTE[jobIndex % JOB_COLOR_PALETTE.length]
            : '#2221BB';
        addMarker(
          su.site.id,
          `${su.site.id}-${usage.waypointType}`,
          usage.waypointType,
          lat,
          lng,
          su.site.name ?? '',
          firstJob?.id ?? '',
          firstJob?.jobId ?? '',
          letter,
          color,
          isDefaultSite,
        );
      }
    }
  }

  for (const [, entry] of siteWaypointMap) {
    if (entry.types.has(WaypointType.PICKUP) && entry.types.has(WaypointType.DROP_OFF)) {
      for (const marker of entry.markers) {
        result.push({ ...marker, waypointType: 'both' });
      }
    } else {
      result.push(...entry.markers);
    }
  }

  return result;
}
